import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { ensureBootstrapped } from './api/client';
import AppShell from './screens/AppShell';
import HomeScreen from './screens/HomeScreen';
import TasksScreen from './screens/TasksScreen';
import WalletScreen from './screens/WalletScreen';
import ProfileScreen from './screens/ProfileScreen';
import FlowScreen from './screens/flow/FlowScreen';
import VideoUpload from './screens/pantene/VideoUpload';
import ModerationStatus from './screens/pantene/ModerationStatus';
import ProductScreen from './screens/ProductScreen';
import HelpScreen from './screens/HelpScreen';
import OfertaScreen from './screens/OfertaScreen';
import RequisitesScreen from './screens/RequisitesScreen';
import FindsScreen from './screens/FindsScreen';
import StatsScreen from './screens/StatsScreen';
import InboxScreen from './screens/InboxScreen';
import NotFoundScreen from './screens/NotFoundScreen';
import CreatorOnboardingScreen from './screens/creator/CreatorOnboardingScreen';
import StatusScreen from './screens/creator/StatusScreen';
import BuyerOnboardingScreen from './screens/onboarding/BuyerOnboardingScreen';
import { FirstLaunchGate } from './screens/onboarding/FirstLaunchGate';
import { stashPendingDeeplink } from './screens/onboarding/store';
import { usePlatform } from './providers/PlatformProvider';

// FlowGallery is dev-only — lazy loaded and excluded from prod bundle
// because the import is guarded by import.meta.env.DEV.
const FlowGallery = import.meta.env.DEV
  ? lazy(() => import('./dev/FlowGallery'))
  : null;

// DebugScreen is dev-only — lazy loaded and excluded from prod bundle
// because the import is guarded by import.meta.env.DEV.
const DebugScreen = import.meta.env.DEV
  ? lazy(() => import('./screens/DebugScreen'))
  : null;

/** Parses TG/platform deep-link start_param and navigates accordingly. */
export function DeepLinkHandler() {
  const platform = usePlatform();
  const navigate = useNavigate();

  useEffect(() => {
    const startParam = platform.getStartParam();
    if (typeof startParam === 'string') {
      const flowMatch = /^flow_(.+)$/.exec(startParam);
      if (flowMatch) {
        const target = `/flow/${flowMatch[1]}`;
        // BUG-1165: stash before navigate — FirstLaunchGate may overwrite this
        // navigation with /onboarding redirect; PreparingStep restores stash after.
        stashPendingDeeplink(target);
        navigate(target, { replace: true });
        return;
      }
      const panteneMatch = /^pantene_(.+)$/.exec(startParam);
      if (panteneMatch) {
        const target = `/pantene/${panteneMatch[1]}/upload`;
        stashPendingDeeplink(target);
        navigate(target, { replace: true });
      }
    }
  }, [platform, navigate]);

  return null;
}

/** All application routes — exported so tests can wrap in <MemoryRouter>. */
export function AppRoutes() {
  return (
    <Routes>
      {/* Tab shell — wraps the 4 bottom-tab roots only */}
      <Route element={<AppShell />}>
        <Route index element={<HomeScreen />} />
        <Route path="/tasks" element={<TasksScreen />} />
        <Route path="/wallet" element={<WalletScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
      </Route>
      {/* Sibling routes — no tab bar */}
      <Route path="/inbox" element={<InboxScreen />} />
      {/* Creator onboarding wizard — FTR-1106 */}
      <Route path="/creator-onboarding/*" element={<CreatorOnboardingScreen />} />
      <Route path="/creator-status" element={<StatusScreen />} />
      {/* Buyer onboarding wizard — FTR-1115: L-042 sibling route, NOT nested */}
      <Route path="/onboarding/*" element={<BuyerOnboardingScreen />} />
      <Route path="/flow/:slotId" element={<FlowScreen />} />
      <Route path="/pantene/:slot_id/upload" element={<VideoUpload />} />
      <Route path="/pantene/:slot_id/status" element={<ModerationStatus />} />
      <Route path="/product/:offerId" element={<ProductScreen />} />
      <Route path="/help" element={<HelpScreen />} />
      <Route path="/oferta" element={<OfertaScreen />} />
      <Route path="/requisites" element={<RequisitesScreen />} />
      <Route path="/finds" element={<FindsScreen />} />
      <Route path="/stats" element={<StatsScreen />} />
      {import.meta.env.DEV && FlowGallery && (
        <Route
          path="/dev/gallery"
          element={
            <Suspense fallback={<div>Загрузка галереи...</div>}>
              <FlowGallery />
            </Suspense>
          }
        />
      )}
      {import.meta.env.DEV && DebugScreen && (
        <Route
          path="/debug"
          element={
            <Suspense fallback={null}>
              <DebugScreen />
            </Suspense>
          }
        />
      )}
      {/* Catch-all — MUST be last: react-router matches in declaration order */}
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  );
}

export default function App() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    ensureBootstrapped().finally(() => setReady(true));
  }, []);

  if (!ready) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
        Загрузка…
      </div>
    );
  }

  return (
    <BrowserRouter>
      <DeepLinkHandler />
      <FirstLaunchGate />
      <AppRoutes />
    </BrowserRouter>
  );
}
